import { useWeb3React } from '@web3-react/core'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'
import { getStakingDashboard, getLonBalance, postStake } from '~/apis'
import { StyledFullPageModalContentOverlay } from '~/components/FullPageModal/styled'
import { PositiveInput } from '~/components/Input'
import Spin from '~/components/Loading/Spin'
import { message } from '~/components/Message'
import TradeDetail from '~/components/TradeDetail'
import { TRADE_STATUS, TRADE_TYPE } from '~/components/TradeDetail/types'
import { useGA, useV6SDK, useUpdaterChainId } from '~/hooks'
import { LOCALES } from '~/locales'
import { useModalToggle } from '~/state/application/hooks'
import { ApplicationModal } from '~/state/application/reducer'
import { formatNumber, toBigNumber } from '~/utils/numbers'
import { gasProtect } from '~/utils/validate'
import {
  StyledContentContainer,
  StyledDepositArea,
  StyledEstimateArea,
  StyledDepositBtn,
} from './styled'

const LonDeposit: React.FC<{
  onComplete: () => void
}> = ({ onComplete }) => {
  const { i18n, t } = useTranslation()
  const isZh = i18n.resolvedLanguage === LOCALES.ZH
  const { account } = useWeb3React()
  const chainId = useUpdaterChainId()
  const { signer } = useV6SDK()

  const [inputBalance, setInputBalance] = useState('')
  const [txHash, setTxHash] = useState('')
  const [status, setStatus] = useState<TRADE_STATUS>()
  const { trackClick } = useGA()

  const toggleModalStaking = useModalToggle(ApplicationModal.LON_STAKING)

  const { data: stakingDashboard } = useSWR(
    ['getStakingDashboard', chainId],
    () => getStakingDashboard(chainId),
  )
  const scaleIndex = stakingDashboard?.scaleIndex
  const { data: lonBalance } = useSWR(['getLonBalance', chainId, account], () =>
    getLonBalance(chainId, account),
  )
  const handleGetAllBalance = () => {
    setInputBalance(lonBalance)
  }
  const handleInputChange = (value: string) => {
    setInputBalance(value)
  }

  const lonBalanceBN = toBigNumber(lonBalance)
  const inputBalanceBN = toBigNumber(inputBalance)
  const estimateXLonAmount = inputBalanceBN.dividedBy(toBigNumber(scaleIndex))
  const xLonAmount =
    inputBalance && inputBalanceBN.gt(0) ? estimateXLonAmount : toBigNumber(0)

  const handleConfirmStake = async () => {
    if (inputBalanceBN.gt(lonBalanceBN)) {
      message.error(t('BALANCE_INSUFFICIENT'))
      return
    }
    if (!inputBalance || inputBalanceBN.lte(0)) {
      message.error(t('unvalid_input_amount'))
      return
    }
    setStatus(TRADE_STATUS.UN_BROADCAST)
    try {
      const hash = await postStake(signer, inputBalance, gasProtect)
      if (hash) {
        setTxHash(hash)
        setStatus(TRADE_STATUS.PENDING)
      }
      try {
        const receipt = await signer.publicClient.waitForTransactionReceipt({
          hash,
        })
        if (receipt.status) {
          setStatus(TRADE_STATUS.SUCCESS)
          trackClick('webtl_staking_stake_confirm', {
            is_success: 1,
            amount: inputBalance,
          })
        }
      } catch (e) {
        setStatus(TRADE_STATUS.FAILED)
        const errorMessage =
          typeof e === 'string' ? e : e?.reason || e?.message || e?.code
        if (errorMessage?.toLowerCase()?.includes('user rejected')) {
          message.error(t('user_rejected_message'))
        } else {
          message.error(errorMessage)
        }
        trackClick('webtl_staking_stake_confirm', {
          is_success: 0,
          amount: inputBalance,
          error_message: errorMessage,
        })
      }
    } catch (e) {
      setStatus(undefined)
      toggleModalStaking()
      const errorMessage =
        typeof e === 'string' ? e : e?.reason || e?.message || e?.code
      if (errorMessage?.toLowerCase()?.includes('user rejected')) {
        message.error(t('user_rejected_message'))
      } else {
        message.error(errorMessage)
      }
      trackClick('webtl_staking_stake_confirm', {
        is_success: 0,
        amount: inputBalance,
        error_message: errorMessage,
      })
    }
  }

  if (status && status !== TRADE_STATUS.UN_BROADCAST) {
    return (
      <TradeDetail
        fromToken={`${formatNumber(inputBalanceBN, 2)} LON`}
        sectionData={[
          {
            label: t('current_rate'),
            value: `xLON: LON ≈ ${formatNumber(scaleIndex, 4)}`,
          },
        ]}
        toToken={`≈${formatNumber(xLonAmount, 6)} xLON`}
        tradeType={TRADE_TYPE.STAKING}
        txHash={txHash}
        onSuccess={onComplete}
        status={status}
      />
    )
  }

  return (
    <StyledContentContainer>
      <p className="deposit-modal-title">{t('staking_lon_stake')}</p>
      <section className="description">
        <p>{t('staking_lon_stake_tips_1')}</p>
        <p>{t('staking_lon_stake_tips_2')}</p>
        <p>
          ({t('staking_lon_stake_tips_3')}
          <a
            href={`https://support.tokenlon.im/hc/${
              isZh ? 'zh-cn' : 'en-us'
            }/articles/360059287511`}
            target="_blank"
            rel="noreferrer"
          >
            {t('staking_lon_stake_faq')}
          </a>
          )
        </p>
      </section>
      <StyledDepositArea>
        <p className="balance">
          <span className="label">{t('balance')}</span>
          <span className="value">{formatNumber(lonBalance, 4)} LON</span>
        </p>
        <p className="input-title">{t('staking_input_label_stake')}</p>
        <div className="deposit-input-field">
          <PositiveInput
            className="deposit-numbers"
            onChange={handleInputChange}
            placeholder={t('staking_input_placeholder_stake')}
            value={inputBalance}
          />
          <button className="all-btn" onClick={handleGetAllBalance}>
            {t('staking_amount_all')}
          </button>
        </div>
      </StyledDepositArea>
      <StyledEstimateArea>
        <div className="rate-area">
          <span className="rate-text">{t('current_rate')}</span>
          <span className="rate-value">
            xLON : LON = {formatNumber(scaleIndex, 6)}
          </span>
        </div>
        <div className="gain-area">
          <span className="gain-text">{t('staking_gain')}</span>
          <span className="gain-value">
            {formatNumber(xLonAmount, 6)}
            xLON
          </span>
        </div>
        {/* <span className="estimate-value">≈ ${xLonPriceValue()}</span> */}
      </StyledEstimateArea>
      <StyledDepositBtn className="deposit-btn-area">
        <button className="confirm-deposit-btn" onClick={handleConfirmStake}>
          {!!status ? <Spin /> : t('staking_confirm')}
        </button>
      </StyledDepositBtn>
      {!!status && <StyledFullPageModalContentOverlay />}
    </StyledContentContainer>
  )
}

export default LonDeposit
